import {
  Badge,
  Button,
  cn,
  confirm,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@devhop/ui";
import {
  CalendarDaysIcon,
  Mic2Icon,
  Music2Icon,
  PaletteIcon,
  PencilIcon,
  SparklesIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { useMemo } from "react";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import type { AssignmentDTO, EventShowDTO, RoleFeeKind } from "../../domain/contracts";
import { ROLE_FEE_KIND_LABELS } from "../../domain/contracts";
import {
  compareAssignmentLineKeys,
  filterAssignmentsByTeamView,
  type TeamAssignmentFilter,
} from "../../domain/services/assignment-line-key";

export type { TeamAssignmentFilter };

type EventAssignmentsListProps = {
  assignments: AssignmentDTO[];
  eventShows?: EventShowDTO[];
  totalEventShowCount?: number;
  filter?: TeamAssignmentFilter;
  canUpdate: boolean;
  canDelete: boolean;
  deletePending: boolean;
  getInitials: (name: string) => string;
  onViewPerformer: (performerId: string) => void;
  onEdit: (assignment: AssignmentDTO) => void;
  onDelete: (assignmentId: string) => void | Promise<void>;
};

const FILTER_LABELS: Record<TeamAssignmentFilter, string> = {
  all: "ທັງໝົດ",
  rehearsal: "ຊ້ອມ",
  show: "ໂຊ",
};

const ROLE_ICONS: Record<RoleFeeKind, typeof UserIcon> = {
  performer: Mic2Icon,
  passed_audition_performer: StarIcon,
  choreographer: SparklesIcon,
  stage_choreographer: UsersIcon,
  show_designer: PaletteIcon,
  music_arranger: Music2Icon,
};

function formatMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

function showIndexLabel(showId: string, eventShows: EventShowDTO[]) {
  const index = eventShows.findIndex((show) => show.id === showId);
  return index >= 0 ? `ໂຊ ${index + 1}` : showId.slice(0, 8);
}

function buildShowSelectionMeta(
  assignment: AssignmentDTO,
  eventShows: EventShowDTO[] | undefined,
  totalEventShowCount: number | undefined,
) {
  const selectedIds = assignment.eventShowIds ?? [];
  const selectedCount =
    selectedIds.length > 0 ? selectedIds.length : assignment.showCount;
  const multiShow =
    totalEventShowCount != null && totalEventShowCount >= 2;
  const partial =
    multiShow && selectedCount > 0 && selectedCount < totalEventShowCount;

  const selectedLabels =
    eventShows && selectedIds.length > 0
      ? selectedIds.map((id) => showIndexLabel(id, eventShows))
      : [];

  const tooltipLines =
    eventShows && selectedIds.length > 0
      ? selectedIds
          .map((id) => {
            const show = eventShows.find((item) => item.id === id);
            if (!show) return null;
            return `${showIndexLabel(id, eventShows)} · ${formatDateTimeLocal(show.showDate)}`;
          })
          .filter((line): line is string => line != null)
      : [];

  return {
    selectedCount,
    multiShow,
    partial,
    selectedLabels,
    tooltipLines,
  };
}

function confirmDeleteAssignment(description: string) {
  return confirm({
    title: "ລຶບບັນທັດ",
    description,
    actionText: "ລຶບ",
    ActionProps: { variant: "destructive" },
  });
}

async function runDelete(
  onDelete: (assignmentId: string) => void | Promise<void>,
  assignmentId: string,
) {
  toast.promise(Promise.resolve(onDelete(assignmentId)), {
    loading: "ກຳລັງລຶບ...",
    success: "ລຶບສຳເລັດ",
    error: "ລຶບລົ້ມເຫຼວ",
  });
}

function AssignmentLineRow({
  assignment,
  eventShows,
  totalEventShowCount,
  canUpdate,
  canDelete,
  deletePending,
  onEdit,
  onDelete,
}: {
  assignment: AssignmentDTO;
  eventShows?: EventShowDTO[];
  totalEventShowCount?: number;
  canUpdate: boolean;
  canDelete: boolean;
  deletePending: boolean;
  onEdit: (assignment: AssignmentDTO) => void;
  onDelete: (assignmentId: string) => void | Promise<void>;
}) {
  const RoleIcon = ROLE_ICONS[assignment.roleFeeKind] ?? UserIcon;
  const showMeta = buildShowSelectionMeta(
    assignment,
    eventShows,
    totalEventShowCount,
  );
  const lineKeyLabel =
    assignment.lineKey !== "default" && assignment.lineKey !== "rehearsal"
      ? assignment.lineKey
      : null;

  return (
    <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-1 gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-muted/30">
          <RoleIcon className="size-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-sm">
              {ROLE_FEE_KIND_LABELS[assignment.roleFeeKind]}
            </span>
            {lineKeyLabel ? (
              <Badge variant="outline" className="font-mono text-[10px]">
                {lineKeyLabel}
              </Badge>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {showMeta.multiShow ? (
              showMeta.partial ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="cursor-default gap-1 bg-amber-500/10 text-amber-900 dark:text-amber-200"
                    >
                      {showMeta.selectedCount}/{totalEventShowCount} ໂຊ
                    </Badge>
                  </TooltipTrigger>
                  {showMeta.tooltipLines.length > 0 ? (
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p className="mb-1 font-medium text-xs">ໂຊທີ່ເລືອກ</p>
                      <ul className="space-y-0.5 text-xs">
                        {showMeta.tooltipLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </TooltipContent>
                  ) : null}
                </Tooltip>
              ) : (
                <Badge variant="outline">
                  {showMeta.selectedCount}/{totalEventShowCount} ໂຊ
                </Badge>
              )
            ) : (
              <Badge variant="outline">{assignment.showCount} ໂຊ</Badge>
            )}

            {assignment.rehearsalDays > 0 ? (
              <Badge variant="secondary" className="gap-1">
                <CalendarDaysIcon className="size-3" />
                {assignment.rehearsalDays} ວັນຊ້ອມ
              </Badge>
            ) : null}

            {showMeta.selectedLabels.length > 0 && showMeta.partial ? (
              <span className="text-muted-foreground text-xs">
                {showMeta.selectedLabels.join(" · ")}
              </span>
            ) : null}
          </div>

          {assignment.notes ? (
            <p className="line-clamp-2 text-muted-foreground text-xs">
              {assignment.notes}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 sm:flex-col sm:items-end sm:justify-start">
        <p className="font-semibold text-sm tabular-nums tracking-tight">
          {formatMoney(assignment.grossAmount)}
        </p>

        {(canUpdate || canDelete) && (
          <div className="flex items-center gap-0.5">
            {canUpdate ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="size-8 p-0"
                    aria-label="ແກ້ໄຂ"
                    onClick={() => onEdit(assignment)}
                  >
                    <PencilIcon className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>ແກ້ໄຂ</TooltipContent>
              </Tooltip>
            ) : null}
            {canDelete ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="size-8 p-0 text-destructive hover:bg-destructive/10"
                    aria-label="ລຶບ"
                    disabled={deletePending}
                    onClick={async () => {
                      const ok = await confirmDeleteAssignment(
                        "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບບັນທັດນີ້ອອກຈາກງານ?",
                      );
                      if (ok) await runDelete(onDelete, assignment.id);
                    }}
                  >
                    <TrashIcon className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>ລຶບ</TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function PerformerAssignmentCard({
  performerId,
  performerName,
  rows,
  eventShows,
  totalEventShowCount,
  canUpdate,
  canDelete,
  deletePending,
  getInitials,
  onViewPerformer,
  onEdit,
  onDelete,
}: {
  performerId: string;
  performerName: string;
  rows: AssignmentDTO[];
  eventShows?: EventShowDTO[];
  totalEventShowCount?: number;
  canUpdate: boolean;
  canDelete: boolean;
  deletePending: boolean;
  getInitials: (name: string) => string;
  onViewPerformer: (performerId: string) => void;
  onEdit: (assignment: AssignmentDTO) => void;
  onDelete: (assignmentId: string) => void | Promise<void>;
}) {
  const subtotal = rows.reduce((sum, row) => sum + row.grossAmount, 0);
  const showLines = rows.filter((row) => row.rehearsalDays === 0).length;
  const rehearsalLines = rows.filter((row) => row.rehearsalDays > 0).length;

  return (
    <article className="overflow-hidden rounded-xl border bg-card shadow-sm transition-shadow hover:shadow-md">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b bg-muted/20 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary text-sm">
            {getInitials(performerName)}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold">{performerName}</p>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-muted-foreground text-xs">
              <span>{rows.length} ບັນທັດ</span>
              {showLines > 0 ? <span>· {showLines} ໂຊ</span> : null}
              {rehearsalLines > 0 ? (
                <span>· {rehearsalLines} ຊ້ອມ</span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <p className="font-semibold text-sm tabular-nums">{formatMoney(subtotal)}</p>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5 px-2.5"
            onClick={() => onViewPerformer(performerId)}
          >
            <UserIcon className="size-3.5" />
            <span className="text-xs">ໂປຣໄຟລ໌</span>
          </Button>
        </div>
      </header>

      <div className="divide-y">
        {rows.map((assignment) => (
          <AssignmentLineRow
            key={assignment.id}
            assignment={assignment}
            eventShows={eventShows}
            totalEventShowCount={totalEventShowCount}
            canUpdate={canUpdate}
            canDelete={canDelete}
            deletePending={deletePending}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </article>
  );
}

function EventAssignmentsSummary({
  performerCount,
  lineCount,
  totalGross,
}: {
  performerCount: number;
  lineCount: number;
  totalGross: number;
}) {
  return (
    <div className="grid gap-px border-b bg-border sm:grid-cols-3">
      <div className="flex flex-col gap-0.5 bg-card px-5 py-3">
        <span className="flex items-center gap-1.5 text-muted-foreground text-xs">
          <UsersIcon className="size-3.5" />
          ນັກສະແດງ
        </span>
        <span className="font-semibold text-lg tabular-nums">{performerCount}</span>
      </div>
      <div className="flex flex-col gap-0.5 bg-card px-5 py-3">
        <span className="text-muted-foreground text-xs">ບັນທັດຄ່າຕົວ</span>
        <span className="font-semibold text-lg tabular-nums">{lineCount}</span>
      </div>
      <div className="flex flex-col gap-0.5 bg-primary/5 px-5 py-3">
        <span className="text-muted-foreground text-xs">ລວມຄ່າຈ້າງ (ຄາດການ)</span>
        <span className="font-semibold text-lg tabular-nums">
          {formatMoney(totalGross)}
        </span>
      </div>
    </div>
  );
}

export function EventTeamAssignmentFilters({
  assignments,
  value,
  onChange,
}: {
  assignments: AssignmentDTO[];
  value: TeamAssignmentFilter;
  onChange: (v: TeamAssignmentFilter) => void;
}) {
  const counts = useMemo(
    () => ({
      all: assignments.length,
      rehearsal: filterAssignmentsByTeamView(assignments, "rehearsal").length,
      show: filterAssignmentsByTeamView(assignments, "show").length,
    }),
    [assignments],
  );

  const options: TeamAssignmentFilter[] = ["all", "rehearsal", "show"];

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-5 py-3">
      <span className="mr-1 text-muted-foreground text-xs">ກັ່ນຕອງ:</span>
      {options.map((opt) => {
        const active = value === opt;
        const count = counts[opt];
        return (
          <Button
            key={opt}
            type="button"
            size="sm"
            variant={active ? "default" : "outline"}
            className={cn("h-8 gap-1.5 text-xs", !active && count === 0 && "opacity-50")}
            disabled={count === 0 && opt !== "all"}
            onClick={() => onChange(opt)}
          >
            {FILTER_LABELS[opt]}
            <span
              className={cn(
                "rounded-full px-1.5 py-0.5 text-[10px] tabular-nums leading-none",
                active ? "bg-primary-foreground/20" : "bg-muted",
              )}
            >
              {count}
            </span>
          </Button>
        );
      })}
    </div>
  );
}

export function EventAssignmentsList({
  assignments,
  eventShows,
  totalEventShowCount,
  filter = "all",
  canUpdate,
  canDelete,
  deletePending,
  getInitials,
  onViewPerformer,
  onEdit,
  onDelete,
}: EventAssignmentsListProps) {
  const filtered = useMemo(
    () => filterAssignmentsByTeamView(assignments, filter),
    [assignments, filter],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, AssignmentDTO[]>();
    for (const assignment of filtered) {
      const list = map.get(assignment.performerId) ?? [];
      list.push(assignment);
      map.set(assignment.performerId, list);
    }
    return [...map.entries()]
      .map(([performerId, rows]) => ({
        performerId,
        performerName: rows[0]?.performerName ?? performerId,
        rows: [...rows].sort((a, b) =>
          compareAssignmentLineKeys(a.lineKey, b.lineKey),
        ),
      }))
      .sort((a, b) => a.performerName.localeCompare(b.performerName));
  }, [filtered]);

  const summary = useMemo(() => {
    const performerIds = new Set(filtered.map((row) => row.performerId));
    return {
      performerCount: performerIds.size,
      lineCount: filtered.length,
      totalGross: filtered.reduce((sum, row) => sum + row.grossAmount, 0),
    };
  }, [filtered]);

  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-muted">
          <UsersIcon className="size-5 text-muted-foreground" />
        </div>
        <p className="font-medium text-sm">
          {filter === "all" ? "ຍັງບໍ່ມີລາຍການ" : `ບໍ່ມີລາຍການໃນກຸ່ມ «${FILTER_LABELS[filter]}»`}
        </p>
        {filter !== "all" ? (
          <p className="max-w-sm text-muted-foreground text-xs">
            ລອງເລືອກກຸ່ມ «ທັງໝົດ» ເພື່ອເບິ່ງນັກສະແດງທັງໝົດ
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <EventAssignmentsSummary
        performerCount={summary.performerCount}
        lineCount={summary.lineCount}
        totalGross={summary.totalGross}
      />

      <div className="space-y-3 px-5 py-4">
        {grouped.map((group) => (
          <PerformerAssignmentCard
            key={group.performerId}
            performerId={group.performerId}
            performerName={group.performerName}
            rows={group.rows}
            eventShows={eventShows}
            totalEventShowCount={totalEventShowCount}
            canUpdate={canUpdate}
            canDelete={canDelete}
            deletePending={deletePending}
            getInitials={getInitials}
            onViewPerformer={onViewPerformer}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </TooltipProvider>
  );
}
